import React, { FunctionComponent, useMemo, useState } from "react"

import { Flex, FlexItem } from "components/Flex"

import Modal from "components/Modal/Modal"
import FormField from "components/FormField/FormField"
import Button from "components/Button/Button"
import SelectSearch from "components/Select/SelectSearch"
import Highlighter from "components/Highlighter/Highlighter"
import AssetService from "services/AssetService"
import { useTranslation } from "react-i18next"
import Badge from "components/Badge/Badge"
import { useNavigate, useLocation } from "react-router-dom"
import Routes from "@/routes"
import { GroupedControl } from "config/constants"
import Select from "components/Select/Select"
import { AssetReferenceNumberType } from "models/Asset"
import { getAssetReferenceNumberSaleTypeLabel } from "utils/utils"
import { Grid } from "components/Grid"

interface IReferenceNumberSelectModalProps {
    onClose(): void
}

const ReferenceNumberSelectModal: FunctionComponent<React.PropsWithChildren<IReferenceNumberSelectModalProps>> = ({
    onClose,
}) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const [type, setType] = useState(AssetReferenceNumberType.Initial)

    async function getReferenceNumbers(search: string) {
        return Promise.all([AssetService.getReferenceNumbers({ search, type })])
    }

    function onReferenceNumberSelect(item?: { referenceNumber: string, assetsCount: number }) {
        if (!item) return
        navigate(Routes.manageReferenceValues.getPath({ referenceNumber: encodeURIComponent(item.referenceNumber) }, { backUrl: location.pathname, type }))
        onClose()
    }

    function renderOption(option: { referenceNumber: string, assetsCount: number }, highlight: string) {
        return (
            <Flex justifyContent="space-between" alignItems="center" className="w-100">
                <div>
                    <Highlighter text={option.referenceNumber} highlight={highlight} />
                </div>
                <FlexItem className="ml-1" shrink={0}>
                    <Badge>{t('common.xAssets', { count: option.assetsCount })}</Badge>
                </FlexItem>
            </Flex>
        )
    }

    const typeOptions = useMemo(() => Object.values(AssetReferenceNumberType)
        .map(_type => (
            {
                value: _type,
                label: getAssetReferenceNumberSaleTypeLabel(_type)
            }
        )), [])

    return (
        <Modal
            title={t('selectReferenceNumber.title')}
            onClose={onClose}
            maxWidth="708px"
            footer={
                <Flex justifyContent="flex-end">
                    <Button
                        theme="secondary"
                        label={t('common.cancel')}
                        onClick={onClose}
                    />
                </Flex>
            }>
            <FormField
                label={t('selectReferenceNumber.fieldLabel')}>
                <Grid columns={"1fr 3fr"}>
                    <Select
                        grouped={GroupedControl.left}
                        options={typeOptions}
                        value={type}
                        onChange={(item) => setType(item?.value ?? AssetReferenceNumberType.Initial)}
                        isInModal
                    />
                    <SelectSearch
                        getOptions={getReferenceNumbers}
                        placeholder={type === AssetReferenceNumberType.Initial ? t('selectReferenceNumber.fieldInitialPlaceholder') : t('selectReferenceNumber.fieldPlaceholder')}
                        onChange={onReferenceNumberSelect}
                        optionTemplate={renderOption}
                        dropdownIndicator
                        grouped={GroupedControl.right}
                        isInModal
                    /></Grid>
            </FormField>
        </Modal>
    )
}

export default ReferenceNumberSelectModal
